/**
 * Dates the page shows, in the reader's language (uiv2 7c).
 *
 * Until 7c: 24 `toLocaleDateString()` with no locale (the BROWSER's language),
 * `'en-US'`/`'en-CA'` literals, and 47 dayjs display patterns (`'MMM D, YYYY'`
 * …) in English word order with English month names — "Sep 27, 2026" to a
 * French reader. Each shape here is one `Intl.DateTimeFormat` in
 * `formatLocale()`: "Sep 27, 2026", "27 sept. 2026", "27 سبتمبر 2026".
 *
 * A date-only string ("2026-09-27") is a CALENDAR day, parsed as local
 * midnight through dayjs — `new Date('2026-09-27')` is UTC midnight, which is
 * the 26th in Toronto. Machine formats (`'YYYY-MM-DD'`) stay dayjs.
 */
import dayjs, { type Dayjs } from 'dayjs';
import i18next from 'i18next';
import type { TimeFormat } from '@bakedshift/shared';
import { formatLocale } from './locale';

export type DateInput = string | number | Date | Dayjs;

const ABSENT = '—';

function toDate(d: DateInput | null | undefined): Date | null {
  // `dayjs(undefined)` is NOW; `dayjs(null)` and `dayjs('')` are invalid.
  if (d === undefined) return null;
  const parsed = dayjs(d);
  return parsed.isValid() ? parsed.toDate() : null;
}

/**
 * The reader's clock (uiv2 BE5, SET-10): `users.time_format`, set from the
 * signed-in user by AuthProvider on each render. `auto` leaves it to the
 * language (12-hour in English, 24-hour in French and Arabic).
 */
let timeFormat: TimeFormat = 'auto';

export function setTimeFormatPreference(next: TimeFormat): void {
  timeFormat = next;
}

/**
 * `hour12` for an `Intl` time: the reader's choice when they made one, else
 * `siteDefault` — a site that always wrote 24-hour keeps doing so under `auto`.
 */
export function hour12Option(siteDefault?: boolean): { hour12?: boolean } {
  if (timeFormat === '12h') return { hour12: true };
  if (timeFormat === '24h') return { hour12: false };
  return siteDefault === undefined ? {} : { hour12: siteDefault };
}

function fmt(options: Intl.DateTimeFormatOptions) {
  return (d: DateInput | null | undefined): string => {
    const date = toDate(d);
    if (!date) return ABSENT;
    // Read per call, not per module load: the preference arrives after sign-in.
    const withClock = options.hour === undefined ? options : { ...options, ...hour12Option() };
    return new Intl.DateTimeFormat(formatLocale(), withClock).format(date);
  };
}

const monthLong = fmt({ month: 'long' });

/** A month, 1–12, by its name: "July" · "juillet" · "يوليو" (uiv2 BE5's fiscal-year picker). */
/**
 * A time zone's generic name in a language — "Eastern Time", "heure de l’Est"
 * — or null when the runtime has none. The zone picker labels with it, since
 * the IANA ids name one city per zone (release review, v2026.9.28).
 */
export function fmtZoneGenericName(timeZone: string, lng: string): string | null {
  return new Intl.DateTimeFormat(formatLocale(lng), { timeZone, timeZoneName: 'longGeneric' })
    .formatToParts(new Date(0))
    .find((p) => p.type === 'timeZoneName')?.value ?? null;
}

export function fmtMonthName(month: number): string {
  return monthLong(new Date(2000, month - 1, 1));
}

/** "Sep 27, 2026" · "27 sept. 2026" · "27 سبتمبر 2026". */
export const fmtDate = fmt({ month: 'short', day: 'numeric', year: 'numeric' });

/** "Sep 27, 2026, 2:05 PM" · "27 sept. 2026, 14 h 05". */
export const fmtDateTime = fmt({ month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

/** "Sep 27, 2026, 2:05:09 PM" — an audit trail's instant. */
export const fmtDateTimeSeconds = fmt({
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit',
});

/** "Sun, Sep 27, 2026". */
export const fmtWeekdayDate = fmt({ weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

/** "Sep 27" · "27 sept.". */
export const fmtMonthDay = fmt({ month: 'short', day: 'numeric' });

/** "Sep" · "sept.". */
export const fmtMonth = fmt({ month: 'short' });

/** "Sep 2026" · "sept. 2026". */
export const fmtMonthYear = fmt({ month: 'short', year: 'numeric' });

/** "Sep 26" · "sept. 26" — a chart axis's month, where width is short. */
export const fmtMonthYearShort = fmt({ month: 'short', year: '2-digit' });

/** "Sun" · "dim." — a calendar cell's weekday. */
export const fmtWeekdayShort = fmt({ weekday: 'short' });

/** "September 2026" · "septembre 2026". */
export const fmtMonthYearLong = fmt({ month: 'long', year: 'numeric' });

/** "2:05 PM" · "14 h 05". */
export const fmtTime = fmt({ hour: 'numeric', minute: '2-digit' });

/** The aggregation a report bucket was grouped by (`ReportAggregation` and Explore's). */
export type PeriodGrain = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

/**
 * A report bucket's name from its KEY, in the reader's language. The server
 * used to word it (Postgres `TO_CHAR(…, 'Mon YYYY')`, `'W35 Aug 24'`, Explore's
 * `Week of …` in `toLocaleDateString('en-US')`), so every bucket label was
 * English. Keys: `2026-09-27` (day, or a week's first day), `2026-W39` (ISO
 * week), `2026-09` (month), `2026-Q3`, `2026`.
 */
export function fmtPeriod(key: string, grain?: PeriodGrain): string {
  const t = i18next.t.bind(i18next);
  const iso = /^(\d{4})-W(\d{2})$/.exec(key);
  if (iso) {
    const [, year, week] = iso;
    // ISO week 1 contains January 4th; its Monday starts the week.
    const jan4 = dayjs(`${year}-01-04`);
    const monday = jan4.subtract((jan4.day() + 6) % 7, 'day').add(Number(week) - 1, 'week');
    return t('common.period.week', { week: Number(week), start: fmtMonthDay(monday) });
  }
  const quarter = /^(\d{4})-Q([1-4])$/.exec(key);
  if (quarter) return t('common.period.quarter', { quarter: Number(quarter[2]), year: quarter[1] });
  if (/^\d{4}$/.test(key)) return key;
  if (/^\d{4}-\d{2}$/.test(key)) return fmtMonthYear(`${key}-01`);
  if (grain === 'weekly') return t('common.period.weekOf', { start: fmtMonthDay(key) });
  return fmtMonthDay(key);
}

/**
 * A stored CALENDAR day ("2026-09-27", a statement period, a source date),
 * formatted at UTC midnight IN UTC — a reader west of Greenwich would
 * otherwise see the day before. `lng` for a caller that states its language
 * (pure helpers tested per locale); the reader's by default.
 */
function utcDay(day: string): Date {
  return new Date(`${day.slice(0, 10)}T00:00:00Z`);
}

const DAY_UTC: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' };

/** "Jan 28, 2025" · "28 janv. 2025" — a calendar day. */
export function fmtCalendarDay(day: string, lng?: string): string {
  return new Intl.DateTimeFormat(formatLocale(lng), DAY_UTC).format(utcDay(day));
}

/** "Mar 2 – 31, 2026" · "2–31 mars 2026" — two calendar days as one range. */
export function fmtCalendarRange(start: string, end: string, lng?: string): string {
  return new Intl.DateTimeFormat(formatLocale(lng), DAY_UTC).formatRange(utcDay(start), utcDay(end));
}

/** "Mar 2026" · "mars 2026" — the month of a calendar day. */
export function fmtCalendarMonth(day: string, lng?: string): string {
  return new Intl.DateTimeFormat(formatLocale(lng), { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(utcDay(day));
}
